import {
  CreateResourceData,
  GetResourceData,
  UpdateResourceData,
  GetUserData,
  IResourceRepository,
  IUserRepository,
} from '../repository/interfaces';
import { ICurriculumUseCase } from './interfaces';

/**
 * Curriculum/Resources Use Case
 * Handles resource creation, purchasing, and management
 * User Stories: 8, 9
 */
export class CurriculumUseCase implements ICurriculumUseCase {
  constructor(
    private resourceRepo: IResourceRepository,
    private userRepo: IUserRepository
  ) {
    // Removed BaseUseCase
  }

  /**
   * Create a curriculum resource
   * User Story 8: As a seller (permitted tiers), I can create a curriculum item with title, description, cover image, price, file(s)/video(s) or external (YouTube/Vimeo)
   */
  async createResource(
    resourceData: CreateResourceData,
    creatorId: string,
    files?: Array<{
      type: 'thumbnail' | 'video' | 'file';
      file: File;
    }>
  ): Promise<{
    resource: GetResourceData;
    uploadedFiles: Array<{
      type: string;
      url: string;
    }>;
  }> {
    try {
      // Verify creator has permission (check subscription tier)
      const creator = await this.userRepo.getUserById(creatorId);
      if (!creator) {
        throw new Error('Creator not found');
      }

      // Check if creator has appropriate tier to create resources
      const allowedTiers = ['professional', 'instructor', 'enterprise'];
      if (!allowedTiers.includes(creator.subscriptionTier || '')) {
        throw new Error('Your subscription tier does not allow creating resources');
      }

      // Upload files if provided
      const uploadedFiles: Array<{ type: string; url: string }> = [];

      for (const fileInfo of files || []) {
        let url: string;

        try {
          if (fileInfo.type === 'thumbnail') {
            url = await this.resourceRepo.uploadResourceThumbnail('temp', fileInfo.file);
          } else if (fileInfo.type === 'video') {
            url = await this.resourceRepo.uploadResourceVideo('temp', fileInfo.file);
          } else {
            url = await this.resourceRepo.uploadResourceFile('temp', fileInfo.file);
          }

          uploadedFiles.push({
            type: fileInfo.type,
            url
          });
        } catch (error) {
          this.logError(error, `Failed to upload ${fileInfo.type} file`);
          throw new Error(`Failed to upload ${fileInfo.type} file`);
        }
      }

      // Update resource data with uploaded file URLs
      const finalResourceData = { ...resourceData };

      for (const uploadedFile of uploadedFiles) {
        if (uploadedFile.type === 'thumbnail') {
          finalResourceData.thumbnailUrl = uploadedFile.url;
        } else if (uploadedFile.type === 'video') {
          finalResourceData.resourceUrl = uploadedFile.url;
        } else if (uploadedFile.type === 'file') {
          finalResourceData.resourceUrl = uploadedFile.url;
        }
      }

      // Set instructorId to creator
      finalResourceData.instructorId = creatorId;

      // Create the resource
      const resource = await this.resourceRepo.createResource(finalResourceData);

      return {
        resource,
        uploadedFiles
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Purchase a curriculum resource
   * User Story 9: As a buyer, after purchase I can download/stream from my Library; failed downloads never loop to pricing
   */
  async purchaseResource(
    resourceId: string,
    buyerId: string,
    paymentMethod?: string
  ): Promise<{
    resource: GetResourceData;
    purchase: any; // Would be a purchase/order record
    downloadUrls: Array<{
      type: string;
      url: string;
      expiresAt?: Date;
    }>;
  }> {
    try {
      // Get resource
      const resource = await this.resourceRepo.getResourceById(resourceId);
      if (!resource) {
        throw new Error('Resource not found');
      }

      // Check if resource is published
      if (!resource.isPublished) {
        throw new Error('Resource is not available for purchase');
      }

      // Check if user already owns this resource
      // In a real implementation, you'd check purchase history
      const buyer = await this.userRepo.getUserById(buyerId);
      if (!buyer) {
        throw new Error('Buyer not found');
      }

      // Process payment (simplified - would integrate with Stripe)
      if (resource.price && resource.price > 0) {
        this.logInfo(`Processing payment of $${resource.price} for resource ${resourceId}`);

        // Mock payment processing
        if (!paymentMethod) {
          throw new Error('Payment method required for paid resources');
        }

        // In a real implementation, you'd create a payment intent and confirm payment
        const paymentSuccess = Math.random() > 0.1; // 90% success rate for demo
        if (!paymentSuccess) {
          throw new Error('Payment failed');
        }
      }

      // Create purchase record (simplified)
      const purchase = {
        id: `purchase_${Date.now()}`,
        userId: buyerId,
        resourceId,
        amount: resource.price || 0,
        purchasedAt: new Date(),
        paymentMethod: paymentMethod || 'free'
      };

      // Generate download URLs (with expiration for security)
      const downloadUrls = [];
      const expirationTime = new Date();
      expirationTime.setHours(expirationTime.getHours() + 24); // 24 hours

      if (resource.resourceUrl) {
        downloadUrls.push({
          type: 'resource',
          url: resource.resourceUrl,
          expiresAt: expirationTime
        });
      }

      if (resource.thumbnailUrl) {
        downloadUrls.push({
          type: 'thumbnail',
          url: resource.thumbnailUrl,
          expiresAt: expirationTime
        });
      }

      // Record the download/view
      await this.resourceRepo.recordResourceView({
        userId: buyerId,
        resourceId,
        timeSpentSeconds: 0
      });

      this.logInfo(`Resource ${resourceId} purchased by user ${buyerId}`);

      return {
        resource,
        purchase,
        downloadUrls
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get user's resource library
   * User Story 9: As a buyer, after purchase I can download/stream from my Library
   */
  async getUserLibrary(
    userId: string,
    filters?: {
      contentType?: string;
      category?: string;
      purchasedAfter?: Date;
    }
  ): Promise<{
    resources: Array<{
      resource: GetResourceData;
      purchaseDate: Date;
      lastAccessed?: Date;
      downloadCount: number;
    }>;
    stats: {
      totalResources: number;
      totalValue: number;
      mostViewedCategory: string;
    };
  }> {
    try {
      // In a real implementation, you'd query purchase records
      // For demo, we'll get all published resources as if user owns them
      const allResources = await this.resourceRepo.getResources({
        isPublished: true
      });

      // Filter resources (simplified)
      let userResources = allResources;

      if (filters?.contentType) {
        userResources = userResources.filter(r => r.contentType === filters.contentType);
      }

      // Mock purchase data
      const resourcesWithPurchaseData = userResources.map(resource => ({
        resource,
        purchaseDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000), // Random date within last 30 days
        lastAccessed: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000) : undefined,
        downloadCount: Math.floor(Math.random() * 10)
      }));

      // Sort by purchase date (most recent first)
      resourcesWithPurchaseData.sort((a, b) => b.purchaseDate.getTime() - a.purchaseDate.getTime());

      // Calculate stats
      const totalResources = resourcesWithPurchaseData.length;
      const totalValue = resourcesWithPurchaseData.reduce((sum, item) =>
        sum + (item.resource.price || 0), 0
      );

      // Mock most viewed category
      const categories = ['video', 'document', 'image', 'audio', 'text'];
      const mostViewedCategory = categories[Math.floor(Math.random() * categories.length)];

      return {
        resources: resourcesWithPurchaseData,
        stats: {
          totalResources,
          totalValue,
          mostViewedCategory
        }
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Download resource file
   * User Story 9: As a buyer, after purchase I can download/stream from my Library; failed downloads never loop to pricing
   */
  async downloadResource(
    resourceId: string,
    userId: string,
    fileType: 'resource' | 'thumbnail' = 'resource'
  ): Promise<{
    downloadUrl: string;
    expiresAt: Date;
    fileName: string;
    fileSize?: number;
  }> {
    try {
      // Verify user owns this resource (simplified - would check purchase records)
      const resource = await this.resourceRepo.getResourceById(resourceId);
      if (!resource) {
        throw new Error('Resource not found');
      }

      // Check if user has access to this resource
      // In a real implementation, you'd verify purchase record exists

      let downloadUrl: string;
      let fileName: string;

      if (fileType === 'thumbnail' && resource.thumbnailUrl) {
        downloadUrl = resource.thumbnailUrl;
        fileName = `thumbnail_${resource.title}.jpg`;
      } else if (fileType === 'resource' && resource.resourceUrl) {
        downloadUrl = resource.resourceUrl;
        fileName = this.generateFileName(resource);
      } else {
        throw new Error(`${fileType} not available for this resource`);
      }

      // Generate secure download URL with expiration
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 1); // 1 hour expiration

      // Record download
      await this.resourceRepo.recordResourceDownload({
        userId,
        resourceId,
      });

      this.logInfo(`Resource ${resourceId} downloaded by user ${userId}`);

      return {
        downloadUrl,
        expiresAt,
        fileName,
        fileSize: resource.fileSize
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Search and browse resources
   */
  async searchResources(
    searchCriteria: {
      query?: string;
      contentType?: string;
      difficultyLevel?: string;
      category?: string;
      instructor?: string;
      isPremium?: boolean;
      priceMin?: number;
      priceMax?: number;
      tags?: string[];
    },
    options: {
      sortBy?: 'newest' | 'oldest' | 'priceLow' | 'priceHigh' | 'rating' | 'popular';
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<{
    resources: GetResourceData[];
    total: number;
    facets: {
      contentTypes: Array<{ type: string; count: number }>;
      categories: Array<{ category: string; count: number }>;
      difficultyLevels: Array<{ level: string; count: number }>;
      priceRanges: Array<{ range: string; count: number }>;
    };
  }> {
    try {
      // Get all published resources
      let resources = await this.resourceRepo.getResources({
        isPublished: true
      });

      // Apply search filters
      if (searchCriteria.query) {
        const query = searchCriteria.query.toLowerCase();
        resources = resources.filter(r =>
          r.title.toLowerCase().includes(query) ||
          r.description?.toLowerCase().includes(query) ||
          r.tags?.some(tag => tag.toLowerCase().includes(query))
        );
      }

      if (searchCriteria.contentType) {
        resources = resources.filter(r => r.contentType === searchCriteria.contentType);
      }

      if (searchCriteria.difficultyLevel) {
        resources = resources.filter(r => r.difficultyLevel === searchCriteria.difficultyLevel);
      }

      if (searchCriteria.isPremium !== undefined) {
        resources = resources.filter(r => r.isPremium === searchCriteria.isPremium);
      }

      if (searchCriteria.priceMin !== undefined) {
        resources = resources.filter(r => (r.price || 0) >= searchCriteria.priceMin!);
      }

      if (searchCriteria.priceMax !== undefined) {
        resources = resources.filter(r => (r.price || 0) <= searchCriteria.priceMax!);
      }

      // Sort results
      switch (options.sortBy) {
        case 'newest':
          resources.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        case 'oldest':
          resources.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          break;
        case 'priceLow':
          resources.sort((a, b) => (a.price || 0) - (b.price || 0));
          break;
        case 'priceHigh':
          resources.sort((a, b) => (b.price || 0) - (a.price || 0));
          break;
        case 'popular':
        default:
          // Sort by some popularity metric (simplified)
          resources.sort(() => Math.random() - 0.5);
          break;
      }

      // Apply pagination
      const total = resources.length;
      const start = options.offset || 0;
      const limit = options.limit || 20;
      resources = resources.slice(start, start + limit);

      // Generate facets
      const allResources = await this.resourceRepo.getResources({ isPublished: true });

      const contentTypes = this.generateFacet(allResources, 'contentType');
      const categories = this.generateFacet(allResources, 'categoryIds');
      const difficultyLevels = this.generateFacet(allResources, 'difficultyLevel');

      const priceRanges = [
        { range: 'Free', min: 0, max: 0 },
        { range: '$1 - $10', min: 1, max: 10 },
        { range: '$11 - $50', min: 11, max: 50 },
        { range: '$51 - $100', min: 51, max: 100 },
        { range: '$100+', min: 101, max: Infinity }
      ].map(range => ({
        range: range.range,
        count: allResources.filter(r =>
          (r.price || 0) >= range.min && (r.price || 0) <= range.max
        ).length
      }));

      return {
        resources,
        total,
        facets: {
          contentTypes,
          categories,
          difficultyLevels,
          priceRanges
        }
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get resource details with related content
   */
  async getResourceDetails(
    resourceId: string,
    userId?: string
  ): Promise<{
    resource: GetResourceData;
    instructor: GetUserData;
    relatedResources: GetResourceData[];
    userStats?: {
      hasPurchased: boolean;
      hasDownloaded: boolean;
      lastViewed?: Date;
      downloadCount: number;
    };
    reviews: any[]; // Would be review data
  }> {
    try {
      // Get resource
      const resource = await this.resourceRepo.getResourceById(resourceId);
      if (!resource) {
        throw new Error('Resource not found');
      }

      // Get instructor details
      const instructor = await this.userRepo.getUserById(resource.instructorId);
      if (!instructor) {
        throw new Error('Instructor not found');
      }

      // Get related resources (same category or instructor)
      const allResources = await this.resourceRepo.getResources({
        isPublished: true
      });

      const relatedResources = allResources
        .filter(r =>
          r.id !== resourceId &&
          (r.instructorId === resource.instructorId ||
           r.contentType === resource.contentType)
        )
        .slice(0, 4);

      let userStats: any = undefined;
      if (userId) {
        // Mock user stats - would check actual purchase/view/download records
        userStats = {
          hasPurchased: Math.random() > 0.3, // 70% chance user has purchased
          hasDownloaded: Math.random() > 0.5,
          lastViewed: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000) : undefined,
          downloadCount: Math.floor(Math.random() * 5)
        };
      }

      // Mock reviews
      const reviews = Array.from({ length: Math.floor(Math.random() * 10) }, () => ({
        id: `review_${Math.random()}`,
        userId: `user_${Math.random()}`,
        rating: Math.floor(Math.random() * 5) + 1,
        comment: 'Great resource!',
        createdAt: new Date()
      }));

      return {
        resource,
        instructor,
        relatedResources,
        userStats,
        reviews
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Update resource
   */
  async updateResource(
    resourceId: string,
    instructorId: string,
    updates: UpdateResourceData
  ): Promise<GetResourceData> {
    try {
      // Verify instructor owns this resource
      const resource = await this.resourceRepo.getResourceById(resourceId);
      if (!resource) {
        throw new Error('Resource not found');
      }

      if (resource.instructorId !== instructorId) {
        throw new Error('Unauthorized: You do not own this resource');
      }

      // Update resource
      const updatedResource = await this.resourceRepo.updateResource(resourceId, updates);

      this.logInfo(`Resource ${resourceId} updated by instructor ${instructorId}`);

      return updatedResource;
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Delete resource
   */
  async deleteResource(
    resourceId: string,
    instructorId: string
  ): Promise<void> {
    try {
      // Verify instructor owns this resource
      const resource = await this.resourceRepo.getResourceById(resourceId);
      if (!resource) {
        throw new Error('Resource not found');
      }

      if (resource.instructorId !== instructorId) {
        throw new Error('Unauthorized: You do not own this resource');
      }

      // Delete resource (would also need to delete associated files)
      await this.resourceRepo.deleteResource(resourceId);

      this.logInfo(`Resource ${resourceId} deleted by instructor ${instructorId}`);
    } catch (error) { console.error("Error:", error); throw error; }
  }

  // Helper methods
  private generateFacet(resources: GetResourceData[], field: keyof GetResourceData): Array<{ [key: string]: number }> {
    const facetMap = new Map<string, number>();

    resources.forEach(resource => {
      const value = resource[field];
      if (value) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            facetMap.set(item, (facetMap.get(item) || 0) + 1);
          });
        } else {
          facetMap.set(value as string, (facetMap.get(value as string) || 0) + 1);
        }
      }
    });

    return Array.from(facetMap.entries()).map(([key, count]) => ({ [field]: key, count }));
  }

  private generateFileName(resource: GetResourceData): string {
    const extension = this.getFileExtension(resource.contentType);
    return `${resource.title.replace(/[^a-zA-Z0-9]/g, '_')}${extension}`;
  }

  private getFileExtension(contentType: string): string {
    const extensions: { [key: string]: string } = {
      'video': '.mp4',
      'document': '.pdf',
      'image': '.jpg',
      'audio': '.mp3',
      'text': '.txt',
      'interactive': '.html'
    };
    return extensions[contentType] || '.file';
  }
}
